import { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, onSnapshot, getDocs, limit,
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  where, increment, serverTimestamp
} from 'firebase/firestore';
import { uploadImageToR2 } from '../services/r2UploadService';
import { db, auth } from '../services/firebase';

export const useRecipes = (filters = {}) => {
  const [allRecipes, setAllRecipes] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { mealType, cuisine, difficulty, diet, maxTime, search } = filters;
  const dietaryTags = filters.dietaryTags;

  // One-shot fetch — recipes collection doesn't need real-time streaming.
  // Switching from onSnapshot to getDocs eliminates a persistent Firestore
  // channel and prevents the 6-connection browser limit from being hit.
  useEffect(() => {
    let cancelled = false;
    const fetchRecipes = async () => {
      try {
        const q = query(
          collection(db, 'recipes'),
          where('status', '==', 'approved'),
          orderBy('createdAt', 'desc'),
          limit(200)
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          setAllRecipes(snap.docs.map(doc => {
            const data = doc.data();
            return { ...data, firestoreId: doc.id, id: data.id || doc.id };
          }));
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching recipes:', err);
        if (!cancelled) { setError(err.message); setLoading(false); }
      }
    };
    fetchRecipes();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let filtered = [...allRecipes];

    filtered = filtered.filter(r => r.status === 'approved' && r.isVisible === true);

    if (cuisine && cuisine !== 'all') {
      filtered = filtered.filter(r => r.cuisine === cuisine);
    }
    if (mealType && mealType !== 'all') {
      filtered = filtered.filter(r => r.mealType === mealType);
    }
    if (difficulty && difficulty !== 'all') {
      filtered = filtered.filter(r => r.difficulty === difficulty);
    }
    if (diet && diet !== 'all') {
      filtered = filtered.filter(r => {
        const tags = r.dietaryTags || [];
        return tags.some(tag =>
          tag.toLowerCase().replace(/[_\s]/g, '') ===
          diet.toLowerCase().replace(/[_\s]/g, '')
        );
      });
    }
    if (dietaryTags && dietaryTags.length > 0) {
      filtered = filtered.filter(r =>
        dietaryTags.every(tag => (r.dietaryTags || []).includes(tag))
      );
    }
    if (maxTime) {
      filtered = filtered.filter(r => (r.totalTimeMinutes || 999) <= maxTime);
    }
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(r =>
        r.title?.toLowerCase().includes(searchLower) ||
        r.description?.toLowerCase().includes(searchLower) ||
        r.ingredients?.some(ing => ing.name?.toLowerCase().includes(searchLower))
      );
    }

    setRecipes(filtered);
  }, [allRecipes, mealType, cuisine, difficulty, diet, dietaryTags, maxTime, search]);

  return { recipes, allRecipes, loading, error };
};

export const useRecipe = (recipeId) => {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recipeId) { setLoading(false); return; }

    const fetchRecipe = async () => {
      try {
        setLoading(true);
        const recipeDoc = await getDoc(doc(db, 'recipes', recipeId));
        if (recipeDoc.exists()) {
          const data = recipeDoc.data();
          setRecipe({ ...data, firestoreId: recipeDoc.id, id: data.id || recipeDoc.id });
          setError(null);
        } else {
          setError('Recipe not found');
          setRecipe(null);
        }
      } catch (err) {
        console.error('Error fetching recipe:', err);
        setError(err.message);
        setRecipe(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [recipeId]);

  return { recipe, loading, error };
};

export const useFeaturedRecipes = () => {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  // One-shot fetch ordered by rating — no live stream needed.
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const q = query(
          collection(db, 'recipes'),
          where('status', '==', 'approved'),
          orderBy('rating', 'desc'),
          limit(10)
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          const all = snap.docs
            .map(d => ({ ...d.data(), firestoreId: d.id, id: d.data().id || d.id }))
            .filter(r => r.isVisible !== false);
          setFeatured(all);
          setLoading(false);
        }
      } catch (err) {
        console.error('useFeaturedRecipes:', err);
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, []);

  return { featured, loading };
};

export const usePersonalizedRecipes = (careProfiles = []) => {
  const [personalized, setPersonalized] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!careProfiles || careProfiles.length === 0) {
      setPersonalized([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchPersonalized = async () => {
      try {
        const q = query(
          collection(db, 'recipes'),
          where('status', '==', 'approved'),
          orderBy('rating', 'desc'),
          limit(200)
        );
        const snapshot = await getDocs(q);
        if (cancelled) return;
        const all = snapshot.docs
          .map(d => ({ ...d.data(), firestoreId: d.id, id: d.data().id || d.id }))
          .filter(r => r.isVisible !== false);

      // ── iOS CareProfile field names ──────────────────────────────────────
      // profile.allergies           → allergen list (NOT profile.allergens)
      // profile.dietaryRestrictions → avoid ingredients (NOT profile.avoids)
      // profile.tags                → diet style tags (NOT profile.dietaryTags)
      const allergens = new Set();
      const avoidIngredients = new Set();
      careProfiles.forEach(profile => {
        // iOS field: allergies
        (profile.allergies || []).forEach(a => allergens.add(a.toLowerCase().trim()));
        // iOS field: dietaryRestrictions
        (profile.dietaryRestrictions || []).forEach(r => avoidIngredients.add(r.toLowerCase().trim()));
      });

      const filtered = all.filter(r => {
        // Block recipes containing allergens from any care profile
        if (allergens.size > 0) {
          const recipeAllergens = (r.allergens || []).map(a => a.toLowerCase());
          const ingredientNames = Array.isArray(r.ingredients)
            ? r.ingredients.map(i => (typeof i === 'string' ? i : i?.name || '').toLowerCase())
            : [];
          const blocked = [...allergens].some(a =>
            recipeAllergens.includes(a) ||
            ingredientNames.some(n => n.includes(a))
          );
          if (blocked) return false;
        }
        // Block recipes containing avoided ingredients
        if (avoidIngredients.size > 0) {
          const ingredientNames = Array.isArray(r.ingredients)
            ? r.ingredients.map(i => (typeof i === 'string' ? i : i?.name || '').toLowerCase())
            : [];
          const blocked = [...avoidIngredients].some(a =>
            ingredientNames.some(n => n.includes(a))
          );
          if (blocked) return false;
        }
        return true;
      });

        const sorted = [...filtered].sort((a, b) => (b.rating || 0) - (a.rating || 0));
        setPersonalized(sorted.slice(0, 20));
        setLoading(false);
      } catch (err) {
        console.error('usePersonalizedRecipes:', err);
        if (!cancelled) setLoading(false);
      }
    };
    fetchPersonalized();
    return () => { cancelled = true; };
  }, [careProfiles]);

  return { personalized, loading };
};

export const useUserRecipes = (userId) => {
  const [userRecipes, setUserRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setUserRecipes([]); setLoading(false); return; }
    let cancelled = false;
    const fetch = async () => {
      try {
        const q = query(
          collection(db, 'recipes'),
          where('authorId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        if (!cancelled) {
          setUserRecipes(
            snap.docs
              .map(d => ({ ...d.data(), firestoreId: d.id, id: d.data().id || d.id }))
              .filter(r => r.status !== 'deleted')
          );
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching user recipes:', err);
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [userId]);

  return { userRecipes, loading };
};

export const useRecipeFavorites = (userId) => {
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setFavoriteIds(new Set()); setLoading(false); return; }

    const q = query(
      collection(db, 'userFavoriteRecipes'),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ids = new Set(snapshot.docs.map(d => d.data().recipeId));
      setFavoriteIds(ids);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching favorites:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const toggleFavorite = useCallback(async (recipeId) => {
    if (!userId) return;
    const docId = `${userId}_${recipeId}`;
    const favRef = doc(db, 'userFavoriteRecipes', docId);
    const recipeRef = doc(db, 'recipes', recipeId);

    if (favoriteIds.has(recipeId)) {
      await deleteDoc(favRef);
      await updateDoc(recipeRef, { favoriteCount: increment(-1) }).catch(() => {});
    } else {
      await setDoc(favRef, { userId, recipeId, addedAt: serverTimestamp() });
      await updateDoc(recipeRef, { favoriteCount: increment(1) }).catch(() => {});
    }
  }, [userId, favoriteIds]);

  return { favoriteIds, loading, toggleFavorite };
};

export const submitRecipe = async (formData, imageFile) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated');

  let imageUrl = null;
  const recipeId = doc(collection(db, 'recipes')).id;

  if (imageFile) {
    const upload = await uploadImageToR2(imageFile, 'recipe', recipeId, 'primary');
    imageUrl = upload.urls.medium;
  }

  const prepTime = parseInt(formData.prepTime) || 0;
  const cookTime = parseInt(formData.cookTime) || 0;

  const recipe = {
    title: formData.title.trim(),
    description: formData.description.trim(),
    imageUrl,
    imageURL: imageUrl,
    authorId: user.uid,
    authorName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
    isUserGenerated: true,
    isPublic: true,
    status: 'approved',
    isVisible: true,
    prepTime,
    prepTimeMinutes: prepTime,
    cookTime,
    cookTimeMinutes: cookTime,
    totalTimeMinutes: prepTime + cookTime,
    servings: parseInt(formData.servings) || 1,
    mealType: formData.mealType?.toLowerCase() || null,
    cuisine: formData.cuisine || null,
    difficulty: formData.difficulty?.toLowerCase() || null,
    dietaryTags: (formData.dietaryTags || []).map(t => t.toLowerCase()),
    recipeTags: formData.recipeTags || [],
    ingredients: formData.ingredients || [],
    steps: formData.steps || [],
    rating: 0,
    reviewCount: 0,
    favoriteCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, 'recipes', recipeId), recipe);
  return recipeId;
};

export const updateRecipe = async (firestoreId, formData, newImageFile) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated');

  let imageUrl = formData.imageUrl || formData.imageURL || null;

  if (newImageFile) {
    const upload = await uploadImageToR2(newImageFile, 'recipe', firestoreId, 'primary');
    imageUrl = upload.urls.medium;
  }

  const prepTime = parseInt(formData.prepTime) || 0;
  const cookTime = parseInt(formData.cookTime) || 0;

  await updateDoc(doc(db, 'recipes', firestoreId), {
    title: formData.title.trim(),
    description: formData.description.trim(),
    imageUrl,
    imageURL: imageUrl,
    prepTime,
    prepTimeMinutes: prepTime,
    cookTime,
    cookTimeMinutes: cookTime,
    totalTimeMinutes: prepTime + cookTime,
    servings: parseInt(formData.servings) || 1,
    mealType: formData.mealType?.toLowerCase() || null,
    cuisine: formData.cuisine || null,
    difficulty: formData.difficulty?.toLowerCase() || null,
    dietaryTags: (formData.dietaryTags || []).map(t => t.toLowerCase()),
    recipeTags: formData.recipeTags || [],
    ingredients: formData.ingredients || [],
    steps: formData.steps || [],
    updatedAt: serverTimestamp(),
  });
};

export const softDeleteRecipe = async (firestoreId) => {
  await updateDoc(doc(db, 'recipes', firestoreId), {
    status: 'deleted',
    isVisible: false,
    updatedAt: serverTimestamp(),
  });
};

export const saveDraft = (draftData) => {
  try {
    localStorage.setItem('nutrius_recipe_draft', JSON.stringify({
      ...draftData,
      savedAt: new Date().toISOString(),
    }));
    return true;
  } catch (e) {
    console.error('Failed to save draft:', e);
    return false;
  }
};

export const loadDraft = () => {
  try {
    const raw = localStorage.getItem('nutrius_recipe_draft');
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
};

export const deleteDraft = () => {
  localStorage.removeItem('nutrius_recipe_draft');
};

export const hasDraftSaved = () => {
  return !!localStorage.getItem('nutrius_recipe_draft');
};

export const getRecipeImageURL = (recipe) => {
  if (recipe?.images && Array.isArray(recipe.images) && recipe.images.length > 0) {
    const primary = recipe.images.find(img => img.isPrimary);
    return primary?.urls?.medium || recipe.images[0]?.urls?.medium || null;
  }
  return recipe?.imageURL || recipe?.imageUrl || null;
};